import { useEffect, useState } from 'react';
import { Loader2, Send, Users } from 'lucide-react';
import { getRecipients, sendAnnouncement, findNomineeAccounts } from '@generated/server/announce';
import { getStaffPass } from '@generated/hooks/staffPass';
import { getStarsReport } from '@generated/server/stars';

type Recipient = Awaited<ReturnType<typeof getRecipients>>['recipients'][number];
type GroupType = 'Hot Prospect' | 'Red Hook Ads' | 'Village Ads' | 'Classifieds' | 'House Account' | 'Mailing Account';
const GROUPS: GroupType[] = ['Hot Prospect', 'Red Hook Ads', 'Village Ads', 'Classifieds', 'House Account', 'Mailing Account'];
const URL = 'https://ad-portal-gbrook8344s-team-company.v.monday.app';
const BATCH = 40;
const BALLOT_PLACEHOLDER = '[PASTE BALLOT LINK HERE]';
const DEFAULT_BALLOT = 'https://bit.ly/4AJgYbv';
type Stars = Awaited<ReturnType<typeof getStarsReport>>;
function starsEmail(r: Stars, ballot: string) {
  const final = new Date() >= new Date(2026, 10, 11);
  const lines = r.categories.filter((c) => c.top.length).map((c) => {
    const [a, b, d] = c.top;
    return `${c.label}\n  ${final ? 'Winner' : 'Leader'}: ${a?.name} (${a?.votes})${b ? `\n  2nd: ${b.name} (${b.votes})` : ''}${d ? `\n  3rd: ${d.name} (${d.votes})` : ''}`;
  }).join('\n\n');
  const subject = final ? 'The Red Hook Stars winners are in!' : "Red Hook Stars: here's who's leading — there's still time to vote";
  const body = `Hello neighbor,

${final ? `The votes are counted! ${r.total} of you voted in the Red Hook Stars Community Appreciation Awards. Here are the winners, with the runners-up:` : `${r.total} ballots are in for the Red Hook Stars Community Appreciation Awards, and the races are close. Voting closes November 10, so here's where things stand:`}

${lines}

${final ? 'Congratulations to everyone, and thank you for voting.' : `Not happy with the standings? Cast your vote, and please share the ballot with friends, customers and neighbors:\n${ballot || BALLOT_PLACEHOLDER}`}

While you're there, take a look at our new Star-Revue portal for advertising, classifieds, subscriptions, mugs, news tips and more:
${URL}

George Fiala
Publisher, Red Hook Star-Revue`;
  return { subject, body };
}

const DEFAULT_SUBJECT = 'Introducing our new Star-Revue portal: one place for everything';
const DEFAULT_BODY = `Hello,

I'm happy to introduce our new online portal, an all-in-one place to work with your friendly neighborhood papers, the Red Hook Star-Revue and The Village Star-Revue:

${URL}

It's open day or night, on your phone or computer. Here's what you can do there:

ADVERTISE YOUR BUSINESS
- See our ad sizes and rates and build a print campaign in minutes, or get a recommended campaign
- Need help with your ad? Sketch it right on the page in your ad's exact size, add your words, and upload photos or your logo. Our production department designs it for you at no extra charge
- Promote an event, or sponsor Brennan's Report, our monthly video show
- Pay by card, PayPal or Zelle, and receive your contract by email

CLASSIFIEDS, SUBSCRIPTIONS & MERCH
- Place a classified ad, subscribe (or send a gift subscription), and check out our Star-Revue shirts and merch

HAVE YOUR SAY
- Send us a news tip or story idea, anonymously if you prefer
- Write a letter to the editor

JOIN THE TEAM
- We're looking for ad salespeople, telephone sales, and freelance writers, photographers and artists (we pay for printed submissions)

DIRECT MAIL
- Through Select Mail, our direct mail company, we print and mail postcards, newsletters, annual reports and catalogs, bulk mail books and promotional items, and can get you targeted mailing lists by neighborhood, income or buying habits

Every print ad also runs on star-revue.com and in our monthly email newsletter.

Take a look: ${URL}

Questions? Just reply to this email or call me at 917-652-9128. And if you need more personal care, let me know a good time and we'll come by.

George Fiala
Publisher, Star-Revue`;

export function Announce() {
  const [types, setTypes] = useState<GroupType[]>(['Hot Prospect', 'Red Hook Ads', 'Village Ads', 'Classifieds']);
  const [base, setList] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [opened, setOpened] = useState<number[]>([]);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [body, setBody] = useState(DEFAULT_BODY);
  const [testTo, setTestTo] = useState('gbrook8344@gmail.com');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState<{ sent: number; failed: string[]; total: number } | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [brooklyn, setBrooklyn] = useState(false);
  const [ballot, setBallot] = useState(DEFAULT_BALLOT);
  const [starsBusy, setStarsBusy] = useState(false);
  const [starsErr, setStarsErr] = useState<string | null>(null);
  const [nominees, setNominees] = useState<Recipient[]>([]);
  const [nomInfo, setNomInfo] = useState<string | null>(null);
  const list: Recipient[] = ([...base, ...nominees.filter((n) => !base.some((b) => b.email === n.email)).map((n) => ({ ...n, type: `Stars: ${n.type || 'nominee'}` }))]).filter((r) => !/nyc|henry public|cinema newsletter|chez moi|bass (and|&) bourbon|bandujo|atlantic ave(nue)? bid/i.test(r.name));
  const loadStars = async () => {
    setStarsBusy(true); setStarsErr(null);
    try {
      const r = await getStarsReport({ data: { pass: getStaffPass() } });
      if (!r) throw new Error('empty');
      const e = starsEmail(r, ballot.trim());
      setSubject(e.subject); setBody(e.body); setBrooklyn(true);
      const names = r.categories.flatMap((c) => c.top.map((t) => t.name)).filter((n) => n.trim().length >= 3 && n.trim().length <= 120);
      setNomInfo('Looking up leaders and runners-up in your accounts…');
      findNomineeAccounts({ data: { pass: getStaffPass(), names } })
        .then((n) => { const f = n?.found ?? []; setNominees(f); setNomInfo(`Found ${f.length} leader / runner-up businesses with an email in your accounts (of ${n?.searched ?? names.length} names).`); })
        .catch((err) => { console.error(err); setNomInfo('Could not look up the leaders in your accounts.'); });
    } catch (e) { console.error(e); setStarsErr('Could not read the latest ballots. Check the spreadsheet is still shared, then try again.'); }
    finally { setStarsBusy(false); }
  };
  useEffect(() => { if (ballot.trim()) setBody((b) => b.replace(BALLOT_PLACEHOLDER, ballot.trim())); }, [ballot]);

  useEffect(() => {
    if (!brooklyn && types.length === 0) { setList([]); setLoading(false); return; }
    let on = true;
    setLoading(true); setLoadErr(null);
    getRecipients({ data: { pass: getStaffPass(), types, allButVillage: brooklyn } })
      .then((r) => { if (on) setList(r?.recipients ?? []); })
      .catch((e) => { console.error(e); if (on) setLoadErr('Could not load your contacts. Please try again.'); })
      .finally(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [types, brooklyn]);

  const send = async (emails: string[]) => {
    setSending(true); setConfirm(false);
    const state = { sent: 0, failed: [] as string[], total: emails.length };
    setProgress({ ...state });
    try {
      for (let i = 0; i < emails.length; i += 20) {
        const batch = emails.slice(i, i + 20);
        try {
          const r = await sendAnnouncement({ data: { pass: getStaffPass(), to: batch, subject, body } });
          (r?.results ?? []).forEach((x) => (x.ok ? state.sent++ : state.failed.push(x.email)));
        } catch (e) { console.error(e); state.failed.push(...batch); }
        setProgress({ ...state, failed: [...state.failed] });
      }
    } finally { setSending(false); }
  };

  const [msgCopied, setMsgCopied] = useState(false);
  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(`${subject}\n\n${body}`); setMsgCopied(true); setTimeout(() => setMsgCopied(false), 2500); }
    catch (e) { console.error(e); }
  };
  const [copied, setCopied] = useState(false);
  const copyEmails = async () => {
    const text = list.map((r) => r.email).join(', ');
    try { await navigator.clipboard.writeText(text); }
    catch {
      const ta = document.createElement('textarea'); ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } finally { document.body.removeChild(ta); }
    }
    setCopied(true); setTimeout(() => setCopied(false), 2500);
  };

  const toggle = (t: GroupType) => setTypes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  return (
    <section className="grid gap-8 lg:grid-cols-[1fr_320px]">
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Staff · Email announcement</div>
        <h2 className="font-[family-name:var(--font-heading)] text-4xl font-semibold">Email your contacts</h2>
        <p className="mt-1 text-sm text-muted-foreground">Sent from your Gmail, one email per contact — no one sees anyone else's address.</p>

        <div className="mt-6 border-2 border-foreground bg-[hsl(var(--brand-yellow))] p-4">
          <div className="text-sm font-bold">Red Hook Stars email</div>
          <p className="mt-1 text-xs">Fills in the subject and message with the latest standings from the live ballot sheet, and selects all Brooklyn contacts.</p>
          <label htmlFor="ann-ballot" className="mt-3 block text-xs font-medium">Ballot link to share</label>
          <input id="ann-ballot" value={ballot} onChange={(e) => setBallot(e.target.value)} placeholder="https://forms.gle/…" className="mt-1 h-11 w-full border border-foreground bg-background px-3 text-sm" />
          <button type="button" disabled={starsBusy} onClick={() => void loadStars()} className="mt-3 inline-flex min-h-11 items-center gap-2 border-2 border-foreground bg-background px-4 text-sm font-semibold disabled:opacity-50">{starsBusy && <Loader2 className="h-4 w-4 animate-spin" />}Use latest Red Hook Stars results</button>
          {starsErr && <p className="mt-2 text-sm text-destructive">{starsErr}</p>}
          {nomInfo && <p className="mt-2 text-xs font-semibold">{nomInfo}</p>}
          {body.includes(BALLOT_PLACEHOLDER) && <p className="mt-2 text-xs font-semibold text-primary">Paste the ballot link above before sending.</p>}
        </div>
        <label className="mt-6 block text-sm font-medium" htmlFor="ann-subject">Subject</label>
        <input id="ann-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 h-11 w-full border border-input bg-background px-3 text-sm" />
        <label className="mt-4 block text-sm font-medium" htmlFor="ann-body">Message</label>
        <textarea id="ann-body" value={body} onChange={(e) => setBody(e.target.value)} rows={18} className="mt-1 w-full border border-input bg-background p-3 font-mono text-sm leading-relaxed" />

        <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
          <div className="min-w-0 flex-1">
            <label className="block text-xs font-medium" htmlFor="ann-test">Send a test first</label>
            <input id="ann-test" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} className="mt-1 h-11 w-full border border-input bg-background px-3 text-sm" />
          </div>
          <button disabled={sending || !testTo.includes('@') || !subject || !body} onClick={() => void send([testTo])} className="min-h-11 border border-foreground px-4 text-sm font-semibold disabled:opacity-50">Send test</button>
          <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(testTo)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`} target="_blank" rel="noreferrer" className="inline-flex min-h-11 items-center border-2 border-foreground bg-[hsl(var(--brand-yellow))] px-4 text-sm font-semibold">Open test in Gmail</a>
          <button type="button" onClick={() => void copyMessage()} className="min-h-11 border border-foreground px-4 text-sm font-semibold">{msgCopied ? "Copied!" : "Copy message"}</button>
        </div>
      </div>

      <aside className="space-y-4 lg:sticky lg:top-6 lg:self-start">
        <div className="border-2 border-foreground bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-semibold"><Users className="h-4 w-4" />Who gets it</div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button onClick={() => setBrooklyn((b) => !b)} disabled={sending} className={`min-h-9 px-3 text-xs font-semibold ${brooklyn ? 'bg-primary text-primary-foreground' : 'border border-primary text-primary hover:bg-muted'}`}>All Brooklyn (except Village Ads, Kitty Leads &amp; Mailing Accounts)</button>
            {GROUPS.map((t) => (
              <button key={t} onClick={() => toggle(t)} disabled={sending || brooklyn} className={`min-h-9 px-3 text-xs font-semibold disabled:opacity-40 ${types.includes(t) ? 'bg-foreground text-background' : 'border border-border hover:bg-muted'}`}>{t}</button>
            ))}
          </div>
          <div className="mt-4 font-[family-name:var(--font-heading)] text-3xl tabular-nums">{loading ? <Loader2 className="h-6 w-6 animate-spin" /> : list.length}</div>
          <div className="text-xs text-muted-foreground">contacts with an email address (duplicates removed)</div>
          {loadErr && <p className="mt-2 text-sm text-destructive">{loadErr}</p>}
          <ul className="mt-3 max-h-48 overflow-y-auto border-t border-border text-xs">
            {list.map((r) => <li key={r.id} className="flex justify-between gap-2 border-b border-border py-1.5"><span className="truncate">{r.name}</span><span className="shrink-0 text-muted-foreground">{r.type}</span></li>)}
          </ul>

          {!confirm ? (
            <button disabled={sending || loading || list.length === 0 || !subject || !body || body.includes(BALLOT_PLACEHOLDER)} onClick={() => setConfirm(true)} className="mt-4 inline-flex min-h-11 w-full items-center justify-center gap-2 bg-primary text-sm font-semibold text-primary-foreground disabled:opacity-50">
              <Send className="h-4 w-4" />Send to {list.length} contacts
            </button>
          ) : (
            <div className="mt-4 space-y-2 border border-primary p-3 text-sm">
              <p>Send this email to <strong>{list.length}</strong> contacts now? This can't be undone.</p>
              <div className="flex gap-2">
                <button onClick={() => void send(list.map((r) => r.email))} className="min-h-11 flex-1 bg-primary text-sm font-semibold text-primary-foreground">Yes, send</button>
                <button onClick={() => setConfirm(false)} className="min-h-11 flex-1 border border-border text-sm">Cancel</button>
              </div>
            </div>
          )}

          {progress && (
            <div className="mt-4 text-sm" aria-live="polite">
              {sending && <div className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Sending… {progress.sent + progress.failed.length} of {progress.total}</div>}
              {!sending && <div className="font-semibold">Done: {progress.sent} sent{progress.failed.length ? `, ${progress.failed.length} failed` : ''}.</div>}
              {!sending && progress.failed.length > 0 && <p className="mt-1 break-all text-xs text-muted-foreground">Not sent: {progress.failed.join(', ')}</p>}
              {!sending && progress.failed.length > 0 && <p className="mt-1 text-xs font-semibold text-primary">The automatic sender didn&apos;t go through. Use “Open test in Gmail” or the Gmail message buttons below instead.</p>}
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Gmail allows about 500 emails a day from a regular account.</p>

        {list.length > 0 && (
          <div className="border border-border bg-card p-4">
            <div className="text-sm font-semibold">Or send it from Gmail yourself</div>
            <p className="mt-1 text-xs text-muted-foreground">Each button opens a ready-to-send Gmail message with up to {BATCH} contacts in <strong>Bcc</strong> (they can't see each other). Just press Send in Gmail.</p>
            <div className="mt-3 flex flex-col gap-2">
              {Array.from({ length: Math.ceil(list.length / BATCH) }, (_, i) => {
                const chunk = list.slice(i * BATCH, (i + 1) * BATCH);
                const href = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent('gbrook8344@gmail.com')}&bcc=${encodeURIComponent(chunk.map((r) => r.email).join(','))}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                return (
                  <a key={i} href={href} target="_blank" rel="noreferrer" onClick={() => setOpened((o) => [...new Set([...o, i])])} className={`inline-flex min-h-11 items-center justify-between border px-3 text-sm font-semibold hover:bg-muted ${opened.includes(i) ? "border-border text-muted-foreground" : "border-foreground"}`}>
                    <span>{opened.includes(i) ? "✓ Opened · " : ""}Gmail message {i + 1}</span><span className="text-xs font-normal text-muted-foreground">{chunk.length} contacts</span>
                  </a>
                );
              })}
            </div>
            <button type="button" onClick={() => void copyEmails()} className="mt-3 text-xs underline underline-offset-2">{copied ? 'Copied!' : 'Copy all email addresses'}</button>
          </div>
        )}
      </aside>
    </section>
  );
}
